import * as AdMob from "./adMob";
import * as LevelPlay from "./levelplay";
import { AdLoadInfo, AdMobRewardItem, AdMobError } from "@capacitor-community/admob";
import {
  AdLoadInfo as LevelPlayAdLoadInfo,
  RewardItem,
  LevelPlayError as AdError,
} from "@ironfly/capacitor-levelplay";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { EventEmitter, Subscription } from "./event-emitter";

let instance: AdManager | null = null;

/**
 * Get the app-wide AdManager, creating it with the given LogService on first call
 */
export function getAdManager(service: LogService): AdManager {
  if (!instance) {
    instance = new AdManager(service);
  }

  return instance;
}

export enum AdProvider {
  AdMob = "AdMob",
  LevelPlay = "LevelPlay",
}

type UnifiedRewardItem = AdMobRewardItem | RewardItem;
type UnifiedAdError = AdMobError | AdError;
type UnifiedAdLoadInfo = AdLoadInfo | LevelPlayAdLoadInfo;

type AdEvents = {
  loaded: [info: UnifiedAdLoadInfo, provider: AdProvider];
  rewarded: [reward: UnifiedRewardItem, provider: AdProvider];
  failedToLoad: [error: UnifiedAdError, provider: AdProvider];
  dismissed: [provider: AdProvider];
  failedToShow: [error: UnifiedAdError, provider: AdProvider];
};

type AdListener<E extends keyof AdEvents> = (...args: AdEvents[E]) => void;

interface AdModule {
  initialize(): Promise<void>;
  prepareAd(userId?: string): Promise<UnifiedAdLoadInfo>;
  showPreparedAd(): Promise<unknown>;
  subscribeToLoaded(listener: (info: UnifiedAdLoadInfo) => void): Promise<{ remove: () => void }>;
  subscribeToRewarded(
    listener: (reward: UnifiedRewardItem) => void,
  ): Promise<{ remove: () => void }>;
  subscribeToFailedToLoad(
    listener: (error: UnifiedAdError) => void,
  ): Promise<{ remove: () => void }>;
  subscribeToDismissed(listener: () => void): Promise<{ remove: () => void }>;
  subscribeToFailedToShow(
    listener: (error: UnifiedAdError) => void,
  ): Promise<{ remove: () => void }>;
  getErrorMessage(error: UnifiedAdError): string;
}

const MODULES: Record<AdProvider, AdModule> = {
  [AdProvider.AdMob]: AdMob,
  [AdProvider.LevelPlay]: LevelPlay,
};

// Priority order: the first entry is the primary provider. It is initialized,
// tried and reset to first; the rest are fallbacks in this order.
const PROVIDERS = Object.keys(MODULES) as AdProvider[];

class AdManager {
  private currentProvider: AdProvider = PROVIDERS[0];
  private isInitialized = false;
  // Bumped by every showAd()/prepareAd(); a load that finds it changed was
  // superseded and must not touch the provider or prepared state.
  private attemptId = 0;
  private preparedProvider: AdProvider | null = null;
  // The active provider's latest FailedToLoad payload. Native sends it just
  // before rejecting the plugin call, and it carries the real error code.
  private lastLoadError: UnifiedAdError | null = null;

  // Each native event is subscribed to exactly once and re-emitted here after
  // AdManager's own handling, so the outcome never depends on which
  // subscriber registered first.
  private readonly events: EventEmitter<AdEvents>;
  private readonly nativeListenersReady: Promise<void>;

  constructor(private readonly logService: LogService) {
    this.events = new EventEmitter<AdEvents>((error) =>
      this.logService.error("[AdManager] Ad event listener threw: " + error),
    );
    this.nativeListenersReady = this.registerNativeListeners();
  }

  /**
   * Initialize both AdMob and LevelPlay ad systems
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    for (const provider of PROVIDERS) {
      try {
        await MODULES[provider].initialize();
        this.logService.info(`[AdManager] ${provider} initialized successfully`);
      } catch (error) {
        this.logService.error(`[AdManager] Failed to initialize ${provider}: ` + error);
      }
    }

    await this.nativeListenersReady;
    this.isInitialized = true;
  }

  /**
   * Preload an ad without showing it, falling back to the other provider like
   * showAd(). Rejects only once both providers have failed; FailedToLoad
   * subscribers are not notified, since nothing the user asked for failed --
   * a tap still retries via showAd().
   */
  async prepareAd(userId?: string): Promise<void> {
    this.preparedProvider = null;
    const provider = await this.loadWithFallback("preload", userId);
    // null means a newer showAd()/prepareAd() took over; leave its state alone.
    if (provider) {
      this.preparedProvider = provider;
    }
  }

  /**
   * Whether a prepared ad is ready to show on the active provider. Its events
   * are only routed to subscribers while that provider is still active.
   */
  hasPreparedAd(): boolean {
    return this.preparedProvider !== null && this.preparedProvider === this.currentProvider;
  }

  /**
   * Show the ad loaded by prepareAd(). Rejects when there is none for the
   * active provider; callers should fall back to showAd().
   */
  async showPreparedAd(): Promise<void> {
    if (!this.hasPreparedAd()) {
      throw new Error("[AdManager] No prepared ad for the active provider");
    }

    this.preparedProvider = null;
    await MODULES[this.currentProvider].showPreparedAd();
  }

  /**
   * Show an ad, trying current provider first and falling back to the other on
   * a load failure. FailedToLoad subscribers are notified once both providers
   * have failed. A display failure does not fall back: FailedToShow has already
   * released the caller's slot, so a second ad's reward would go uncounted.
   */
  async showAd(userId?: string): Promise<void> {
    this.preparedProvider = null;
    const provider: AdProvider | null = await this.loadWithFallback("show", userId).catch(
      () => null,
    );
    // null means every provider failed or a newer showAd()/prepareAd() took over.
    if (!provider) {
      return;
    }

    try {
      await MODULES[provider].showPreparedAd();
    } catch (error) {
      this.logService.error(`[AdManager] ${provider} show failed: ` + error);
    }
  }

  /**
   * Try the active provider, then the other one. Driven only by the plugin
   * calls' promises -- every native load failure rejects the call -- so the
   * FailedToLoad event never needs to be ordered against the rejection.
   * Resolves to the provider that succeeded, or null if a newer attempt
   * superseded this one; rejects with the last error once both have failed.
   * Both failing resets to AdMob for the next attempt.
   */
  private async loadWithFallback(
    kind: "show" | "preload",
    userId?: string,
  ): Promise<AdProvider | null> {
    // Take the id before waiting, so a newer call still supersedes this one.
    const attemptId = ++this.attemptId;
    // Loaded/FailedToLoad from this load must reach AdManager, so its native
    // listeners have to be registered before the plugin call goes out.
    await this.nativeListenersReady;
    if (this.attemptId !== attemptId) {
      return null;
    }

    // Start from the last provider that worked, then the rest in priority order.
    const start = PROVIDERS.indexOf(this.currentProvider);
    const order = [...PROVIDERS.slice(start), ...PROVIDERS.slice(0, start)];
    let lastError = toAdError("No ad provider available");

    for (const provider of order) {
      this.currentProvider = provider;
      this.lastLoadError = null;

      try {
        await MODULES[provider].prepareAd(userId);
        return this.attemptId === attemptId ? provider : null;
      } catch (error) {
        if (this.attemptId !== attemptId) {
          return null;
        }
        lastError = this.lastLoadError ?? toAdError(error);
        this.logService.error(`[AdManager] ${provider} ${kind} failed: ` + error);
      }
    }

    const lastProvider = this.currentProvider;
    this.currentProvider = PROVIDERS[0]; // Reset to primary for next attempt
    if (kind === "show") {
      this.events.emit("failedToLoad", lastError, lastProvider);
    }
    throw lastError;
  }

  /**
   * Subscribe to ad loaded events from the active provider
   */
  async subscribeToLoaded(listener: AdListener<"loaded">): Promise<Subscription> {
    return this.events.on("loaded", listener);
  }

  /**
   * Subscribe to ad rewarded events from the active provider
   */
  async subscribeToRewarded(listener: AdListener<"rewarded">): Promise<Subscription> {
    return this.events.on("rewarded", listener);
  }

  /**
   * Subscribe to final ad load failures: a showAd() whose providers have all
   * failed. A failure recovered by falling back, or a background prepareAd()
   * failure, is not reported.
   */
  async subscribeToFailedToLoad(listener: AdListener<"failedToLoad">): Promise<Subscription> {
    return this.events.on("failedToLoad", listener);
  }

  /**
   * Subscribe to ad dismissed events from the active provider
   */
  async subscribeToDismissed(listener: AdListener<"dismissed">): Promise<Subscription> {
    return this.events.on("dismissed", listener);
  }

  /**
   * Subscribe to ad failed to show events from the active provider
   */
  async subscribeToFailedToShow(listener: AdListener<"failedToShow">): Promise<Subscription> {
    return this.events.on("failedToShow", listener);
  }

  /**
   * Get error message for any ad error
   */
  getErrorMessage(error: UnifiedAdError, provider: AdProvider): string {
    return MODULES[provider].getErrorMessage(error);
  }

  /**
   * Subscribe once to every native event of both providers. Events from the
   * provider that isn't active are dropped.
   */
  private async registerNativeListeners(): Promise<void> {
    for (const provider of PROVIDERS) {
      const module = MODULES[provider];
      const isActive = () => this.currentProvider === provider;

      try {
        await module.subscribeToLoaded((info) => {
          if (isActive()) {
            this.events.emit("loaded", info, provider);
          }
        });
        await module.subscribeToRewarded((reward) => {
          if (isActive()) {
            this.events.emit("rewarded", reward, provider);
          }
        });
        await module.subscribeToFailedToLoad((error) => {
          // Recorded only; loadWithFallback() reacts to the rejected call.
          if (isActive()) {
            this.lastLoadError = error;
          }
        });
        await module.subscribeToDismissed(() => {
          if (isActive()) {
            this.events.emit("dismissed", provider);
          }
        });
        await module.subscribeToFailedToShow((error) => {
          if (isActive()) {
            this.events.emit("failedToShow", error, provider);
          }
        });
      } catch (error) {
        this.logService.error(`[AdManager] Failed to subscribe to ${provider} events: ` + error);
      }
    }
  }
}

/**
 * Normalize a rejected plugin call into the error shape FailedToLoad
 * subscribers receive from the native event.
 */
function toAdError(error: unknown): UnifiedAdError {
  const { code, message } = (error ?? {}) as { code?: unknown; message?: unknown };
  return {
    code: typeof code === "number" ? code : -1,
    message: typeof message === "string" ? message : String(error),
  };
}
